/**
 * Lazy access to the full lucide icon set as a SINGLE async chunk.
 *
 * We deliberately do NOT use `lucide-react/dynamic` (DynamicIcon /
 * dynamicIconImports) because it code-splits every one of ~1,500 icons into its
 * own tiny chunk — a flood of files that bloats the build and the deploy. Here
 * a single dynamic `import('lucide-react')` pulls the whole `icons` map into one
 * chunk, loaded on demand and cached, so we render any icon by name with just
 * one extra request.
 */

import type { LucideIcon } from 'lucide-react';

let cache: Record<string, LucideIcon> | null = null;
let loading: Promise<Record<string, LucideIcon>> | null = null;

/** Load the lucide icon map (one chunk); memoized after the first call. */
export function loadLucideIcons(): Promise<Record<string, LucideIcon>> {
  if (cache) return Promise.resolve(cache);
  if (!loading) {
    loading = import('lucide-react').then((m) => {
      cache = m.icons as unknown as Record<string, LucideIcon>;
      return cache;
    });
  }
  return loading;
}

/** The map if it's already loaded, else null (for a synchronous first paint). */
export function getCachedLucideIcons(): Record<string, LucideIcon> | null {
  return cache;
}

/** kebab-case stored name → PascalCase key in the lucide `icons` map. */
export function toPascalIconName(kebab: string): string {
  return kebab
    .split('-')
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');
}

/** PascalCase icon-map key → kebab-case stored name (inverse of toPascalIconName). */
export function toKebabIconName(pascal: string): string {
  return pascal
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}
